<template>
	<app-title-bar :icon="['fas', 'fa-truck']" subtitle="Hire Desk" title="Collection List">
		<div class="flex flex-col-reverse sm:flex-row items-center gap-4">
			<depots-dropdown
				v-if="depots"
				v-model="depots"
				size="large"
				required
				update-user
				@update:model-value="getData(true)" />
			<app-button to="/hire-desk" icon="fa-circle-left"> Back </app-button>
		</div>
	</app-title-bar>

	<div class="sm:flex gap-4">
		<app-panel class="grow" title="Collection Details">
			<datatable
				:headers
				:value="items"
				container-class="min-h-[500px]"
				:loading
				clickable
				@row-click="handleRowClick">
				<template #row-status="{ row }">
					<div class="w-7 h-10 inline-flex items-center justify-center">
						<app-tooltip
							:text="getStatusIcon(row, 'collection_status').label"
							position-x="left"
							modifiers="left-[38px]"
							small>
							<app-svg
								:path="`/icons/catalyst/${
									getStatusIcon(row, 'collection_status').icon
								}.svg`"
								:colour="getStatusIcon(row, 'collection_status').colour"
								autoscale />
						</app-tooltip>
					</div>
				</template>

				<template #row-passed_to_driver="{ row }">
					<app-checkbox
						v-if="items.find((obj) => obj.id === row.id)"
						v-model="items.find((obj) => obj.id === row.id)!.passed_to_driver"
						wrapper=""
						name="passed_to_driver"
						:true-value="true"
						:false-value="false"
						:disabled="row.collection_status == 6 || row.collection_status == 2"
						@update:model-value="updated(row)" />
				</template>
			</datatable>

			<div v-if="items.length > 0" class="flex justify-end mt-4">
				<app-button :disabled="!changed" @click="save"> Save </app-button>
			</div>
		</app-panel>
	</div>
</template>

<script lang="ts" setup>
	import DepotsDropdown from '@/components/partials/dropdowns/DepotsDropdown.vue'
	import { ref, computed, onMounted } from 'vue'
	import type { Header, Row } from '@/data/types/datatable'
	import AppSvg from '@/components/base/AppSvg.vue'
	import ContractService from '@/services/contract'
	import useAuthStore from '@/stores/auth'
	import useOperatorStore from '@/stores/operators'
	import { useRouter } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import { formatContractDates } from '@/utils/date'
	import { getStatusIcon } from '@/composables/status'
	import type { CollectionContract } from '@/data/schema/contract'
	import CollectionService from '@/services/collection'

	// Resources
	const contracts = new ContractService()
	const collections = new CollectionService()
	const toaster = useToasterStore()
	const router = useRouter()
	const auth = useAuthStore()
	const operators = useOperatorStore()

	// Data
	const depots = ref<string | null>(null)
	const items = ref<CollectionContract[]>([])
	const loading = ref(true)

	const headers: Header[] = [
		{ value: 'status', text: 'Status', props: { sortable: true } },
		{ value: 'collection_number', text: 'Coll No', props: { sortable: true } },
		{ value: 'account_name', text: 'Customer Name' },
		{ value: 'journey_number', text: 'Journey number' },
		{ value: 'full_address', text: 'Site / Del address' },
		{ value: 'collection_date', text: 'Coll Date', props: { sortable: true } },
		{ value: 'haulier', text: 'Haulier' },
		{ value: 'passed_to_driver', text: 'Passed To Driver' }
	]

	const changed = computed(() => {
		return items.value.some((item) => item.changed)
	})

	function handleRowClick(item: Row) {
		if (item && item.id) {
			router.push(`/hire-desk/collection-item/${item.collection_number}`)
		}
	}

	const getData = (updateOperators = false) => {
		loading.value = true

		if (updateOperators) {
			operators.set(auth!.user!.current_depot!)
		}

		if (depots.value) {
			contracts
				.getHireDeskArea('collections', depots.value!, 1, 1000)
				.then((res) => {
					const formattedItems = formatContractDates(res.data.data)

					items.value = formattedItems.map((obj) => ({
						...obj,
						passed_to_driver:
							obj.status[0].collection_status == 6 ||
							obj.status[0].collection_status == 2,
						collection_status: obj.status[0].collection_status,
						changed: false
					}))

					loading.value = false
				})
				.catch(() => {
				
					toaster.error({ text: 'An error occurred retrieving collections' })
					loading.value = false
				})
		} else {
			toaster.error({ text: 'Invalid depot' })
		}
	}

	function save() {
		const changedItems = items.value.filter((item) => item.changed === true)

		toaster.working({
			text: 'Saving collections...',
			dismissable: false,
			timeout: 0
		})

		changedItems.forEach((collection) => {
			collections
				.updateCollection(collection.collection_number, {
					assigned_to: collection.assigned_to,
					collection_status: collection.assigned_to
						? 2
						: collection.passed_to_driver
							? 6
							: undefined
				})
				.then(() => {
					toaster.clear()
					toaster.success({
						text: 'Changes saved successfully'
					})
					getData()
				})
				.catch((error) => {
					toaster.error({
						text: 'Error occurred saving changes'
					})
					console.error(error)
				})
		})
	}

	/**
	 * Items have been updated
	 */
	function updated(item: CollectionContract) {
		const index = items.value.findIndex((i) => i.id == item.id)

		if (index !== -1) {
			items.value[index].changed = !items.value[index].changed
		}
	}

	onMounted(() => {
		if (auth.user && auth.user.current_depot) {
			depots.value = auth.user.current_depot
		}

		getData(true)
	})
</script>
